import { Stack } from '@mui/material';
import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useSchedules from 'v2/hooks/useSchedules/useSchedules';
import WeekScheduleFields from '../../components/WeekScheduleFields';
import { EditScheduleFormValues, buildWeekSchedule, getFormDefaults } from '../components/editScheduleUtils';

interface Props {
  visitorGroup: VisitorGroup;
}

const EditScheduleDrawer = ({ visitorGroup }: Props) => {
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const [error, setError] = useState<string>();
  const { updateAccessGrant } = useSchedules({
    translation,
    handleFetchError: setError
  });

  const grant = visitorGroup.accessGrants[0];

  const {
    handleSubmit,
    getValues,
    control,
    trigger,
    formState: { isSubmitting }
  } = useForm<EditScheduleFormValues>({
    mode: 'onTouched',
    defaultValues: getFormDefaults(grant.accessSchedule.weekDays)
  });

  const onSubmit = async (data: EditScheduleFormValues) => {
    setError(undefined);
    const result = await updateAccessGrant(grant.id, {
      scheduleType: 'weekSchedule',
      weekSchedule: buildWeekSchedule(data)
    });
    if (!result.success) return;
    showSnackbar(translation.saved_schedule, { severity: 'success' });
    closeDrawer();
  };

  return (
    <DrawerTemplate
      id="edit-group-schedule-drawer"
      loading={isSubmitting}
      title={translation.edit_schedule}
      primaryAction={{
        label: translation.save_schedule,
        onClick: handleSubmit(onSubmit),
        loading: isSubmitting,
        id: 'save-button'
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-button' }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <WeekScheduleFields control={control} getValues={getValues} trigger={trigger} />
      </Stack>
    </DrawerTemplate>
  );
};

export default EditScheduleDrawer;
